import type { Block, DecideReason, FormBlock, FormLine, RowColumn } from '@/kova/spec/types'
import type { ApiClient } from '@/lib/api'

import type { Caller } from './handles'
// TYPE-ONLY, and it has to stay that way. `jurisdictions.ts` imports this
// module at runtime to assemble the screen; a value import back would close the
// loop. Everything both sides need at runtime is in `jurisdictionvocab.ts`.
import type { GateRow } from './jurisdictions'
import { JURISDICTIONS_PATH, KNOWN_CAPABILITIES, jurisdictionPath } from './jurisdictionvocab'
import { rowsFrom, submitWrite, writeRefusal } from './writes'

/**
 * The write controls on Configuration › Jurisdictions & country gating (#213).
 *
 * Five acts, five forms, five audit actions. They live beside the read adapter
 * rather than inside it for the reason every `*controls.ts` module on this
 * surface does: `jurisdictions.ts` is a pure parse of two GETs and stays
 * table-testable with no client and no session, and a file that both reads the
 * gate and moves it is a file where those two jobs get to share state.
 *
 * # Why this exists at all
 *
 * On 2026-08-23 a request to open Spain and Bulgaria for onboarding was
 * satisfied by exec-ing `wallet-admin` inside a staging pod, because the
 * console had no surface for it. Staging turned out to hold ONE jurisdiction
 * row. `adminops/jurisdiction.go` had already written down what that costs:
 *
 * > *"Opening BG for staging required a direct INSERT at a psql prompt. That
 * > meant the FIRST act in the life of every jurisdiction — the one recording
 * > which capabilities the operator is passported for — happened outside the
 * > product, unaudited, by whoever had a database prompt."*
 *
 * That fix moved the act into an audited CLI. This one gives it a surface, and
 * a shell is no longer the only way to enter a market.
 *
 * # Closed is three states, and each control names which one it answers
 *
 * The read side keeps them apart — `gate` as the backend sent it,
 * `onboarding_open` in its own column, uncovered capabilities named rather than
 * left to be inferred. The controls inherit the same discipline, because a
 * remedy offered against the wrong one of the three does nothing and looks like
 * it did something:
 *
 * | The country is | The remedy | The control |
 * |---|---|---|
 * | absent from the table entirely | declare a mandate | **Declare** |
 * | present, `onboarding_open` false | undo a decision somebody took | **Open** |
 * | present and open, capability outside `mandate_scope` | widen the mandate | **Widen** |
 *
 * So there is no "enabled" toggle anywhere in this file, and no control accepts
 * a country the state machine says it cannot act on: a country with no row is
 * not in the Open select, because there is nothing to open.
 *
 * # Declaring and opening are two acts and stay two controls
 *
 * `Create` takes no `onboarding_open` parameter — a new row is CLOSED, always,
 * and the handler answers 201 with `"onboarding_open": false` and `"gate":
 * "closed"` so a console cannot read "created" as "trading". Collapsing the two
 * into one button would put two decisions behind one audit record, and the one
 * that matters to a regulator — when did we start accepting customers there —
 * would be the one that stopped having its own row.
 *
 * # Widening cannot narrow, structurally rather than by care
 *
 * The widen control submits `{add: [...]}` and the narrow control submits
 * `{remove: [...]}`, each built from an EMPTY editor. Neither ever posts the
 * scope this screen rendered. That is the whole reason `/mandate-scope/narrow`
 * is a separate route:
 *
 * > *"a console sending the list it rendered five minutes ago silently
 * > withdraws a capability granted since."*
 *
 * A single form with the live scope pre-filled would be exactly that console.
 * There is no shape of either request here that can express the other's act,
 * matching the two request types on the backend.
 *
 * # One vocabulary
 *
 * `KNOWN_CAPABILITIES` is imported from the read module rather than restated.
 * A second copy is a second chance to drift, and this console has already paid
 * for that once: `internal_transfer` reached the backend at go-wallet#146 and
 * the read side's set was never told, so a correct mandate read as one the
 * console could not understand.
 */

// ---------------------------------------------------------------------------
// Who may write here
// ---------------------------------------------------------------------------

/**
 * `jurisdiction.write`, held by Compliance ALONE.
 *
 * `staff.Holders(PermJurisdictionWrite)` is `[RoleCompliance]` and the backend
 * comment is explicit about the consequence: an ops analyst who can promote
 * catalog candidates all day cannot open a country. That is deliberate —
 * invariant 7 makes country gating the boundary of the registered agent
 * mandate, and the design brief §4 puts regulatory acts with Compliance.
 *
 * Named here as the ROLE an operator would ask for, not as the permission key,
 * because law 18 requires the control be shown disabled with the required role
 * named and `jurisdiction.write` is not something anybody can request.
 */
const PERM_WRITE = 'jurisdiction.write'
const ROLE = 'Compliance'

// ---------------------------------------------------------------------------
// The capability editor
// ---------------------------------------------------------------------------

/**
 * A gloss per capability, so a select does not ask an operator to pick between
 * nine bare identifiers on a licence screen.
 *
 * Keyed off the imported closed set rather than declared as its own list: a
 * capability the backend gains and this table has not glossed still appears,
 * labelled with its own name, instead of silently dropping out of every control
 * on the screen. Missing prose degrades to the identifier; a missing OPTION
 * would degrade to "this capability cannot be granted from the console".
 */
const CAPABILITY_GLOSS: Readonly<Record<string, string>> = {
  receive: 'take money in',
  cash_out: 'pay money out to the customer’s own bank',
  storefront: 'buy from the storefront',
  statements: 'issue statements',
  internal_transfer: 'send to another customer of the programme',
  card: 'card programme (Phase 2)',
  inbound_push: 'inbound push payments (Phase 2)',
  multi_currency: 'hold more than one currency (Phase 2)',
  crypto_ramp: 'crypto on/off ramp (Phase 2)',
}

function capabilityOptions(): readonly (readonly [string, string])[] {
  return KNOWN_CAPABILITIES.map((c) => {
    const gloss = Object.prototype.hasOwnProperty.call(CAPABILITY_GLOSS, c)
      ? CAPABILITY_GLOSS[c]
      : undefined
    return [c, gloss === undefined ? c : `${c} — ${gloss}`] as const
  })
}

/**
 * The capability list, as a `rows` field over a closed select.
 *
 * # Why a repeating editor and not a set of checkboxes
 *
 * There is no multi-select in the block language, and a checkbox per capability
 * would not do. `Form`'s `answered` treats any non-blank value as answered, and
 * an unticked checkbox submits the string `"false"` — so nine required
 * checkboxes would arm the submit button with every one of them off, and post
 * an empty `add` that the backend refuses with a 400 the operator did not need
 * to see.
 *
 * A `rows` field is answered only when at least one row is COMPLETE, so the
 * control stays dead until a capability has actually been chosen. The refusal
 * happens before the request rather than after it, which is what law 21 is
 * asking for.
 *
 * # It starts EMPTY on every control, including the two that move a live mandate
 *
 * Nothing here is pre-filled with what the country currently holds. See the
 * module header: an editor seeded with the rendered scope is the exact failure
 * `/narrow` was split out to make unrepresentable.
 */
const CAPABILITY_COLUMNS: readonly RowColumn[] = [{
  name: 'capability',
  label: 'Capability',
  kind: 'select',
  required: true,
  options: capabilityOptions(),
}]

/**
 * The chosen capabilities, deduplicated, in the order they were chosen.
 *
 * Deduplicated HERE as well as on the backend — `normalizeScope` collapses
 * repeats and calls a doubled entry "a typo with exactly one possible meaning"
 * — because the operator's own result panel should not read `cash_out,
 * cash_out`. The wire is unaffected either way; what this protects is the
 * sentence the operator checks their work against.
 */
function capabilitiesFrom(raw: string | undefined): readonly string[] {
  const out: string[] = []
  const seen = new Set<string>()
  for (const row of rowsFrom(raw, CAPABILITY_COLUMNS)) {
    const value = row['capability']
    if (typeof value !== 'string' || value === '' || seen.has(value)) continue
    seen.add(value)
    out.push(value)
  }
  return out
}

// ---------------------------------------------------------------------------
// Reading a country out of the answer
// ---------------------------------------------------------------------------

function stringAt(o: unknown, key: string): string {
  if (typeof o !== 'object' || o === null) return ''
  const v = (o as Record<string, unknown>)[key]
  return typeof v === 'string' ? v : ''
}

function boolAt(o: unknown, key: string): boolean | null {
  if (typeof o !== 'object' || o === null) return null
  const v = (o as Record<string, unknown>)[key]
  return typeof v === 'boolean' ? v : null
}

/**
 * A string array off an answer, or `null` when the backend did not send one.
 *
 * `null` rather than `[]`, and the panels below say "not stated" for it. An
 * empty mandate is a real and alarming state — a country that accepts onboarding
 * and permits nothing — so rendering an unreadable field as one would report an
 * alarm nobody read.
 */
function listAt(o: unknown, key: string): readonly string[] | null {
  if (typeof o !== 'object' || o === null) return null
  const v = (o as Record<string, unknown>)[key]
  if (!Array.isArray(v)) return null
  return (v as readonly unknown[]).filter((e): e is string => typeof e === 'string')
}

/** The resulting mandate as a line, or the account of why there is none. */
function scopeLine(answer: unknown, label: string): FormLine {
  const scope = listAt(answer, 'mandate_scope')
  if (scope === null) {
    return [label, '— not stated', 'the backend did not echo the resulting scope; re-read the table above']
  }
  return scope.length === 0
    ? [label, 'none — nothing is permitted here', 'a country that permits nothing still accepts onboarding']
    : [label, scope.join(', '), 'this is what is true NOW, not what was asked for']
}

// ---------------------------------------------------------------------------
// What closing a country does, in the console's own words
// ---------------------------------------------------------------------------

/**
 * `authz.Flow` tokens, glossed.
 *
 * The close response carries `stops` and `continues` as MACHINE TOKENS
 * deliberately — the handler's comment says an English clause on the wire would
 * be "a hardcoded string that has been moved rather than removed" — so the
 * translation belongs here. An unglossed token renders verbatim rather than
 * being dropped: a flow this console has not learned about is still a flow the
 * closure affects, and omitting it from the list would make the list read as
 * complete when it is not.
 */
const FLOW_GLOSS: Readonly<Record<string, string>> = {
  registration: 'new sign-ups',
  identity_verification: 'identity verification',
  receive: 'taking money in',
  storefront: 'storefront purchases',
  cash_out: 'cashing out to their own bank',
}

/**
 * One half of the closure consequence, glossed.
 *
 * An absent key and an EMPTY list are told apart, the same way `scopeLine`
 * tells an unread mandate from a mandate covering nothing. They mean opposite
 * things on the half that matters: `continues: []` is "the closure stops
 * absolutely everything, including withdrawals", which is the exact state
 * go-wallet#413 was raised about — and rendering it as "not stated" would hide
 * the alarm behind a shrug. `continues` absent is the console not having been
 * told.
 */
function flows(answer: unknown, key: string): string {
  const raw = listAt(answer, key)
  if (raw === null) return '— not stated'
  if (raw.length === 0) return 'nothing'
  return raw
    .map((f) => (Object.prototype.hasOwnProperty.call(FLOW_GLOSS, f) ? `${FLOW_GLOSS[f] ?? f} (${f})` : f))
    .join(', ')
}

// ---------------------------------------------------------------------------
// Reason codes
// ---------------------------------------------------------------------------

/**
 * Law 21 — a reason code from a managed list, never free text.
 *
 * Separate lists per act rather than one shared one, because the reasons for
 * these acts do not overlap. "The passport notification is now on file" is the
 * only honest reason to open a country and is never a reason to close one; "the
 * regulator withdrew the notification" is the reverse. A shared list would offer
 * every operator the wrong half and invite them to pick the nearest.
 *
 * `reason` is prose on this wire — `takeReason` stores the sentence rather than
 * a column — so these populate the code half of what the form serialises.
 */
const DECLARE_REASONS: readonly DecideReason[] = [
  ['passport_notification_filed', 'The regulator has been notified for this country', ''],
  ['market_entry_planned', 'Declaring the mandate ahead of entering the market', ''],
  ['institution_confirmed_scope', 'The institution confirmed what it will do for us there', ''],
  ['environment_bootstrap', 'Declaring a country in a non-production environment', ''],
]

const OPEN_REASONS: readonly DecideReason[] = [
  ['passport_notification_confirmed', 'The notification is on file and the market may open', ''],
  ['market_entry_approved', 'Entry into this market has been approved', ''],
  ['reopening_after_review', 'Reopening after the review that closed it concluded', ''],
]

const CLOSE_REASONS: readonly DecideReason[] = [
  ['passport_withdrawn', 'The passport notification has been withdrawn', ''],
  ['regulatory_direction', 'A regulator or the institution directed us to stop', ''],
  ['risk_suspension', 'Suspended while a risk concern is investigated', ''],
  ['commercial_exit', 'We are leaving this market', ''],
  ['opened_in_error', 'This country should not have been open', ''],
]

const WIDEN_REASONS: readonly DecideReason[] = [
  ['mandate_covers_capability', 'The registered mandate already covers this and the row did not', ''],
  ['passport_scope_extended', 'The passport notification was extended to cover it', ''],
  ['institution_confirmed_capability', 'The institution confirmed it will do this for us there', ''],
  ['declared_incomplete', 'The country was declared with an incomplete scope', ''],
]

const NARROW_REASONS: readonly DecideReason[] = [
  ['passport_scope_withdrawn', 'The notification no longer covers this capability', ''],
  ['institution_withdrew_capability', 'The institution will no longer do this for us there', ''],
  ['regulatory_direction', 'A regulator or the institution directed us to stop this activity', ''],
  ['granted_in_error', 'This capability should never have been on the mandate', ''],
]

// ---------------------------------------------------------------------------
// Declare
// ---------------------------------------------------------------------------

/**
 * Declaring a country: the first act in a jurisdiction's life.
 *
 * The country is TYPED rather than chosen, and it is the only control here where
 * that is true. Everything else picks from the table, because the country
 * already has a row; this one exists precisely for the countries that do not,
 * and the whole failure it answers is that `BG` was absent from staging and no
 * list on this screen could have offered it.
 *
 * # `account_currency` is the field to be careful about
 *
 * Set-once, and wrong is unrecoverable through the API. `SetAccountCurrency`
 * refuses a change outright (`ErrAccountCurrencyAlreadyStated`) and the reason
 * is not tidiness: `accounts.currency` is written from this column when an
 * account is opened, the institution has issued the IBAN by the time anybody
 * notices, and an existing customer's account cannot be moved to another
 * currency. A country that changed its answer would issue two populations of
 * accounts in two denominations with nothing recording which was which.
 *
 * So this control carries the screen's only typed confirmation, and the phrase
 * is `SET ONCE` rather than `DECLARE` — the string an operator has to type is
 * itself the warning, and the form renders it as "Type SET ONCE to confirm.
 * This cannot be undone."
 *
 * It is NOT offered as a select. The backend accepts any code in
 * `internal/money`'s exponent table, roughly fifty of them; this console's own
 * table records only the exponents that are not 2, so it cannot enumerate that
 * set. A dozen plausible European codes in a dropdown would be a claim about
 * what may be declared that neither module can support, and it would refuse a
 * legitimate country by omission.
 */
function declareForm(api: ApiClient, caller: Caller): FormBlock {
  const blocked = writeRefusal(caller, PERM_WRITE, ROLE, 'Declaring a country')
  return {
    t: 'form',
    title: 'Declare a country',
    lead:
      'Creates the jurisdiction row: the country, when the regulator was notified, what the mandate covers there, and the currency its accounts will be denominated in. '
      + 'It arrives CLOSED — onboarding_open is not a field on this form, and no customer can sign up in this country until somebody opens it as a separate audited act. '
      + 'This is the remedy for the first of the three closed states: a country with no row at all.',
    fields: [
      {
        name: 'country', label: 'Country', kind: 'text', required: true, mono: true,
        placeholder: 'BG',
        note: 'ISO-3166 alpha-2. Typed rather than chosen, because a country that needs declaring is by definition not in the table above.',
      },
      {
        name: 'passport_notified_at', label: 'Passport notified on', kind: 'date', required: true,
        note: 'When the REGULATOR was notified — not today, unless today is when it happened. The backend refuses a future date and never defaults this, because AllowOnboarding reads it: a country can be opened on the strength of this value, and a convenient one would be a fiction in a compliance record.',
      },
      {
        name: 'mandate_scope', label: 'Mandate covers', kind: 'rows', required: true,
        columns: CAPABILITY_COLUMNS,
        note: 'At least one. A country with no capabilities permits nothing and cannot be told apart from a misconfigured one, so the backend refuses an empty scope here. This can be widened later; narrowing it is a separate act.',
      },
      {
        name: 'account_currency', label: 'Account currency', kind: 'text', required: true, mono: true,
        placeholder: 'EUR',
        note: 'SET ONCE. Every account this country issues will be denominated in it, and it cannot be changed afterwards through any endpoint — an account whose IBAN the institution has issued cannot be moved to another currency. ISO-4217, and the backend refuses a code it cannot render amounts in.',
      },
    ],
    reasons: DECLARE_REASONS,
    reasonNote: 'Recorded in the audit row, alongside the mandate scope and the notification date.',
    auditLine: 'jurisdiction.created · actor · jurisdiction:<country> · mandate_scope, passport_notified_at, account_currency, onboarding_open=false, reason',
    cta: 'Declare country',
    ctaNote: 'The country arrives CLOSED. Nobody can onboard there until it is opened, which is the control below.',
    confirm: 'SET ONCE',
    blocked,
    ...(blocked === '' ? { submit: declareSubmit(api) } : {}),
  }
}

function declareSubmit(api: ApiClient) {
  return (v: Readonly<Record<string, string>>) => {
    const country = (v['country'] ?? '').trim().toUpperCase()
    const scope = capabilitiesFrom(v['mandate_scope'])
    return submitWrite(
      () => api.post<unknown>(JURISDICTIONS_PATH, {
        country,
        // The date control yields `YYYY-MM-DD`; the backend parses RFC-3339.
        // Midnight UTC, stated explicitly rather than left to the browser's
        // zone — a notification date that shifts by a day depending on where
        // the operator is sitting is a compliance record that disagrees with
        // itself between two consoles.
        passport_notified_at: `${(v['passport_notified_at'] ?? '').trim()}T00:00:00Z`,
        mandate_scope: scope,
        account_currency: (v['account_currency'] ?? '').trim().toUpperCase(),
        reason: v['reason'] ?? '',
      }),
      'The country is declared, and it is CLOSED',
      (answer) => {
        const open = boolAt(answer, 'onboarding_open')
        return [
          ['country', stringAt(answer, 'country') || country || '—', ''],
          [
            'gate',
            stringAt(answer, 'gate') || (open === false ? 'closed' : '— not stated'),
            'a declared country is always closed; opening it is a separate act',
          ],
          scopeLine(answer, 'mandate covers'),
          [
            'account currency',
            stringAt(answer, 'account_currency') || '— not stated',
            'set once — this cannot be changed through any endpoint',
          ],
          [
            'passport notified',
            stringAt(answer, 'passport_notified_at') || '— not stated',
            '',
          ],
          ['who can onboard here now', 'nobody', 'until the country is opened'],
        ]
      },
    )
  }
}

// ---------------------------------------------------------------------------
// Open
// ---------------------------------------------------------------------------

/**
 * A country the console can say is openable, labelled with why.
 *
 * Three conditions, and every one of them is a refusal to guess:
 *
 * `gate === 'closed'` — an OPEN country has nothing to open, and a `no_mandate`
 * country has no row to open. `setOpen` UPDATEs, so it would answer
 * `ErrNotConfigured` for the latter; the remedy there is Declare.
 *
 * `notificationOnFile === true` — `adminops.setOpen` refuses outright when
 * `passport_notified_at` is null and the schema CHECK refuses it again
 * underneath. The design brief says this screen blocks the open action without
 * one and says why, so the country is not offered and the fail-closed note
 * directly above the control names it and says what to do.
 *
 * `notificationOnFile === null` is NOT offered either, and this is the case a
 * lenient reading gets wrong. It means the payload did not let this console
 * decide — a flag with no date, or a date the epoch guard rejected. An
 * unverified licence basis is not a met one; the read side already refuses to
 * call such a row green for exactly this reason.
 */
function openable(rows: readonly GateRow[]): readonly GateRow[] {
  return rows.filter((r) =>
    r.country !== null && r.gate === 'closed' && r.notificationOnFile === true)
}

function blockedOnNotification(rows: readonly GateRow[]): readonly string[] {
  return rows
    .filter((r) => r.country !== null && r.gate === 'closed' && r.notificationOnFile !== true)
    .map((r) => r.country ?? '')
    .filter((c) => c !== '')
}

function openForm(api: ApiClient, caller: Caller, rows: readonly GateRow[]): FormBlock {
  const session = writeRefusal(caller, PERM_WRITE, ROLE, 'Opening a country')
  const can = openable(rows)
  const waiting = blockedOnNotification(rows)

  const blocked = session !== ''
    ? session
    : can.length > 0
      ? ''
      : waiting.length > 0
        ? `Disabled — no country in this table can be opened. ${waiting.join(', ')} ${waiting.length === 1 ? 'is' : 'are'} closed with no confirmed passport notification on file, and adminops refuses that open outright — not a confirmation dialog, not an override for Admin. File the notification and this control has something to offer.`
        : 'Disabled — no country in this table is closed, so there is none to open. A country that is not in the table at all has no row to open either; declare it first.'

  return {
    t: 'form',
    title: 'Open a country',
    lead:
      'Turns onboarding_open on for a country that already has a jurisdiction row. This is the remedy for the second of the three closed states — a decision somebody took, which this undoes. '
      + 'It grants no capability: what a customer may DO once they are onboarded is the mandate scope, and opening a country changes nothing about it.',
    fields: [
      {
        name: 'country', label: 'Country', kind: 'select', required: true,
        options: can.map((r) => [
          r.country ?? '',
          `${r.country ?? ''} — notification on file ${r.notifiedLabel === '' ? '(date not stated)' : r.notifiedLabel}`,
        ] as const),
        note:
          waiting.length === 0
            ? 'Only countries with a confirmed passport notification are listed, because the backend refuses any other open.'
            : `Only countries with a confirmed passport notification are listed. ${waiting.join(', ')} ${waiting.length === 1 ? 'is' : 'are'} closed and NOT listed here — the fail-closed note above says why and what to do about it. This is not the console hiding a control: the open would be refused.`,
      },
    ],
    reasons: OPEN_REASONS,
    reasonNote: 'Recorded in the audit row. The backend refuses a blank reason on this route.',
    auditLine: 'jurisdiction.opened · actor · jurisdiction:<country> · reason',
    cta: 'Open for onboarding',
    ctaNote: 'Takes effect without a deployment. Serving pods may hold a cached refusal for up to the registry TTL, so the error is briefly in the closed direction and never the other way.',
    blocked,
    ...(blocked === '' ? { submit: gateSubmit(api, 'open') } : {}),
  }
}

// ---------------------------------------------------------------------------
// Close
// ---------------------------------------------------------------------------

/**
 * Closing a country.
 *
 * Never blocked on a passport notification: withdrawing is always permitted,
 * and it is only opening that needs a licence basis. So the only thing that
 * gates this control is whether a country is open at all.
 *
 * # The typed confirmation, and what it can and cannot be
 *
 * `confirm` is a static string on the block while the country is chosen at
 * render time, so it cannot be this country's own code the way a per-case
 * control's is. `FormBlock` warns about exactly that gap — a dialog that only
 * asks whether you are sure cannot tell `eur-out` from `eur-in`.
 *
 * The gap is closed from the other side instead: the country is a SELECT over
 * the open countries rather than a typed field, so the wrong country cannot be
 * reached by a slip of the keyboard — only by choosing it — and the phrase adds
 * the second deliberate gesture. A typed field plus a static phrase would be two
 * gestures and one of them still mis-typeable.
 *
 * # What it does to the customers already there is on the response
 *
 * Until go-wallet#413 the same press also stopped every existing customer in
 * that country from withdrawing their own money, and nothing said so — the
 * first anyone learned of it was a customer contacting support. The gate is
 * split now and the response states which flows stop and which continue. Those
 * arrive as machine tokens and are glossed here; the panel renders both halves,
 * because "what this does not do" is the half an operator cannot infer.
 */
function closeForm(api: ApiClient, caller: Caller, rows: readonly GateRow[]): FormBlock {
  const session = writeRefusal(caller, PERM_WRITE, ROLE, 'Closing a country')
  const open = rows.filter((r) => r.country !== null && r.gate === 'open')

  const blocked = session !== ''
    ? session
    : open.length === 0
      ? 'Disabled — no country in this table is open, so there is none to close.'
      : ''

  return {
    t: 'form',
    title: 'Close a country',
    lead:
      'Turns onboarding_open off. New sign-ups, identity verification, taking money in and storefront purchases stop; cashing out to their own bank does NOT — customers already there can still withdraw their own money, which is the split go-wallet#413 made after a closure quietly stopped that too. '
      + 'The exact list is on the response and is rendered below the act, from the backend rather than from this sentence.',
    fields: [
      {
        name: 'country', label: 'Country', kind: 'select', required: true,
        options: open.map((r) => [r.country ?? '', r.country ?? ''] as const),
        note: 'Chosen rather than typed: only open countries are listed, and a closure aimed at the wrong country is not something a confirmation phrase can catch.',
      },
    ],
    reasons: CLOSE_REASONS,
    reasonNote: 'Recorded in the audit row. This is the act a regulator asks about, so the code is the answer to "why did we stop taking customers there".',
    auditLine: 'jurisdiction.closed · actor · jurisdiction:<country> · reason',
    cta: 'Close to onboarding',
    ctaNote: 'Reversible — reopening is the control above, and it needs a confirmed passport notification. It withdraws no capability: the mandate scope is untouched.',
    confirm: 'CLOSE',
    blocked,
    ...(blocked === '' ? { submit: gateSubmit(api, 'close') } : {}),
  }
}

/**
 * Open and close share a submit, because they share a request body — a reason
 * and nothing else. The DIRECTION is in the route, deliberately, and the two
 * forms above are separate blocks with separate reason lists and separate audit
 * lines. This is where the shapes coincide, not where the acts do.
 */
function gateSubmit(api: ApiClient, verb: 'open' | 'close') {
  return (v: Readonly<Record<string, string>>) => {
    const country = (v['country'] ?? '').trim().toUpperCase()
    return submitWrite(
      () => api.post<unknown>(`${jurisdictionPath(country)}/${verb}`, { reason: v['reason'] ?? '' }),
      verb === 'open' ? 'The country is open for onboarding' : 'The country is closed to onboarding',
      (answer) => {
        const open = boolAt(answer, 'onboarding_open')
        const lines: FormLine[] = [
          ['country', stringAt(answer, 'country') || country || '—', ''],
          [
            'onboarding_open',
            open === null ? '— not stated' : String(open),
            'as the backend reports it, not as this console asked for it',
          ],
          ['gate', stringAt(answer, 'state') || '— not stated', ''],
        ]
        if (verb === 'close') {
          lines.push(['this stops', flows(answer, 'stops'), ''])
          lines.push([
            'this does NOT stop',
            flows(answer, 'continues'),
            'customers already there keep this; the closure is a statement about new customers',
          ])
        } else {
          lines.push([
            'what a customer may do here',
            'whatever the mandate scope covers, and nothing else',
            'opening a country grants no capability',
          ])
        }
        return lines
      },
    )
  }
}

// ---------------------------------------------------------------------------
// Widen and narrow
// ---------------------------------------------------------------------------

/**
 * The countries whose mandate may be moved.
 *
 * Every country with a row, open or closed — a mandate is not a gate, and
 * `WidenMandateScope` neither reads `onboarding_open` nor touches it. Widening a
 * closed country is an ordinary act: it is how a market is made ready before it
 * is entered.
 *
 * Two exclusions, and both are the fail-closed choice rather than a filter for
 * tidiness.
 *
 * `no_mandate` has no row. `WidenMandateScope` answers `ErrNotConfigured` for
 * it, and it should: widening a mandate that was never stated would be stating
 * one, with no notification date and no account currency beside it. That is
 * Declare's act.
 *
 * A `null` gate is a row this console could not resolve — most often because
 * `gate` and `onboarding_open` CONTRADICTED each other, which the read side
 * refuses to settle because it cannot tell which of the two is lying. The
 * mandate column is independent of both, so acting on it would be defensible on
 * the narrow facts. It is still refused: an operator moving a licence claim on a
 * row whose own state is in dispute is doing it without knowing what they are
 * looking at, and the remedy for a contradicted row is somebody reading it, not
 * a second write on top.
 */
function declared(rows: readonly GateRow[]): readonly GateRow[] {
  return rows.filter((r) => r.country !== null && r.gate !== null && r.gate !== 'no_mandate')
}

function scopeLabel(r: GateRow): string {
  const code = r.country ?? ''
  if (r.scope === null) return `${code} — current mandate could not be read`
  if (r.scope.length === 0) return `${code} — currently permits nothing`
  return `${code} — currently ${r.scope.join(', ')}`
}

/**
 * Widening a mandate. It can only ADD.
 *
 * The editor starts empty and submits `add`. Nothing here sends the scope this
 * screen rendered, which is the property `/mandate-scope/narrow` exists to
 * guarantee: a console posting back a list it drew five minutes ago silently
 * withdraws whatever was granted in between.
 *
 * The select's labels show what each country currently holds, so an operator
 * can see what they are adding TO — reading it is useful, sending it is the bug.
 *
 * A widening on its own moves nothing: the capability still cannot be exercised
 * unless the country is also open, which is a second audited act. That is why
 * this is a single-operator control and Close is too — the conservative
 * direction must never wait for a colleague.
 */
function widenForm(api: ApiClient, caller: Caller, rows: readonly GateRow[]): FormBlock {
  const session = writeRefusal(caller, PERM_WRITE, ROLE, 'Widening a mandate')
  const have = declared(rows)
  const blocked = session !== ''
    ? session
    : have.length === 0
      ? 'Disabled — no country in this table has a jurisdiction row to widen. Widening a mandate that was never stated would be stating one, with no notification date and no account currency beside it; declare the country first.'
      : ''

  return {
    t: 'form',
    title: 'Widen a mandate',
    lead:
      'ADDS capabilities to what a country is registered for. This is the remedy for the third closed state — a capability absent from mandate_scope, which is refused even in an open country. '
      + 'It can only add: this control has no form that withdraws a capability, and the list below starts empty rather than pre-filled with what the country holds, so nothing can be dropped by omission.',
    fields: [
      {
        name: 'country', label: 'Country', kind: 'select', required: true,
        options: have.map((r) => [r.country ?? '', scopeLabel(r)] as const),
        note: 'The current mandate is shown so you can see what you are adding to. It is not sent — only the capabilities you name below are.',
      },
      {
        name: 'add', label: 'Capabilities to ADD', kind: 'rows', required: true,
        columns: CAPABILITY_COLUMNS,
        note: 'Only these are sent. A capability the country already holds is accepted and changes nothing — the backend records no audit row for a widening that added nothing, because a row claiming otherwise would be a false record.',
      },
    ],
    reasons: WIDEN_REASONS,
    reasonNote: 'Recorded in the audit row alongside the previous scope and the resulting one, so a decision taken the day before is still explainable.',
    auditLine: 'jurisdiction.mandate_scope_widened · actor · jurisdiction:<country> · added, previous_scope, mandate_scope, reason',
    cta: 'Widen mandate',
    ctaNote: 'This does not open the country. A widened mandate on a closed country still onboards nobody.',
    blocked,
    ...(blocked === '' ? { submit: mandateSubmit(api, 'widen') } : {}),
  }
}

/**
 * Narrowing a mandate. It can only REMOVE.
 *
 * A separate control on a separate route with its own audit action, so "when
 * did we withdraw a capability anywhere" is a query over action codes rather
 * than a scan of attributes on rows that also record the opposite act.
 *
 * # No typed confirmation, and that is the deliberate asymmetry
 *
 * Narrowing is the CONSERVATIVE direction. The backend records why it is a
 * single-operator act — a regulator saying stop is not a request to find a
 * second approver — and the same argument governs the friction this console
 * adds: a mandate mistakenly narrowed is widened again in seconds through the
 * control above, while a mandate that cannot be narrowed is unlicensed activity
 * for as long as the delay lasts.
 *
 * # It cannot empty a mandate, and it does not close the country
 *
 * Narrowing to nothing is refused (`ErrMandateWouldBeEmpty`) and referred to
 * Close, because a country permitting no capability still ACCEPTS onboarding:
 * every customer would sign up successfully and then find that nothing works.
 * The console does not pre-empt that refusal — it does not know the resulting
 * set without doing the backend's arithmetic on a scope that may have moved —
 * but it says so at the control so the refusal is expected rather than confusing.
 */
function narrowForm(api: ApiClient, caller: Caller, rows: readonly GateRow[]): FormBlock {
  const session = writeRefusal(caller, PERM_WRITE, ROLE, 'Narrowing a mandate')
  const have = declared(rows)
  const blocked = session !== ''
    ? session
    : have.length === 0
      ? 'Disabled — no country in this table has a jurisdiction row to narrow.'
      : ''

  return {
    t: 'form',
    title: 'Narrow a mandate',
    lead:
      'WITHDRAWS capabilities from what a country is registered for, from the moment it commits, forwards. It does not reach back: an instruction already authorised under the wider mandate carries its own snapshot of the scope that was live, and one already at the institution completes — we do not hold the funds and cannot recall a submitted payment. '
      + 'An instruction not yet submitted is authorised afresh and meets the narrowed mandate there.',
    fields: [
      {
        name: 'country', label: 'Country', kind: 'select', required: true,
        options: have.map((r) => [r.country ?? '', scopeLabel(r)] as const),
        note: 'The current mandate is shown so you can see what there is to withdraw. It is not sent.',
      },
      {
        name: 'remove', label: 'Capabilities to WITHDRAW', kind: 'rows', required: true,
        columns: CAPABILITY_COLUMNS,
        note: 'Only these are withdrawn. Everything not named here stays. The backend refuses a narrowing that would leave the country permitting nothing and refers it to Close — a country that must stop taking work is closed, not emptied.',
      },
    ],
    reasons: NARROW_REASONS,
    reasonNote: 'Required by the backend on this route specifically, and not on widening. Withdrawing a capability is the act somebody asks about later — "why did BG stop paying out on the 14th" — and the answer has to be on the record rather than in somebody’s memory of the week.',
    auditLine: 'jurisdiction.mandate_scope_narrowed · actor · jurisdiction:<country> · removed, previous_scope, mandate_scope, reason',
    cta: 'Narrow mandate',
    ctaNote: 'This does not close the country. Onboarding continues; only the named capabilities stop. If the intent is to stop taking work there, close it instead.',
    blocked,
    ...(blocked === '' ? { submit: mandateSubmit(api, 'narrow') } : {}),
  }
}

/**
 * The two mandate writes.
 *
 * One function, and the direction decides BOTH the route and the field name —
 * `add` on `/mandate-scope`, `remove` on `/mandate-scope/narrow`. They are
 * derived from the same argument so a body cannot be built for one route with
 * the other's field, which is the console-side half of what two request types
 * on the backend are for.
 */
function mandateSubmit(api: ApiClient, direction: 'widen' | 'narrow') {
  const widening = direction === 'widen'
  const field = widening ? 'add' : 'remove'
  return (v: Readonly<Record<string, string>>) => {
    const country = (v['country'] ?? '').trim().toUpperCase()
    const named = capabilitiesFrom(v[field])
    return submitWrite(
      () => api.post<unknown>(
        widening
          ? `${jurisdictionPath(country)}/mandate-scope`
          : `${jurisdictionPath(country)}/mandate-scope/narrow`,
        { [field]: named, reason: v['reason'] ?? '' },
      ),
      widening ? 'The mandate is widened' : 'The mandate is narrowed',
      (answer) => [
        ['country', stringAt(answer, 'country') || country || '—', ''],
        [
          widening ? 'asked to add' : 'asked to withdraw',
          named.length === 0 ? '—' : named.join(', '),
          '',
        ],
        scopeLine(answer, 'mandate now covers'),
        [
          'onboarding',
          'unchanged',
          widening
            ? 'widening a mandate does not open a country'
            : 'narrowing a mandate does not close one',
        ],
      ],
    )
  }
}

// ---------------------------------------------------------------------------
// The builder
// ---------------------------------------------------------------------------

/**
 * The five controls, in the order an operator meets them.
 *
 * Declare, open, close, widen, narrow — a country's life in sequence, so the
 * screen reads top to bottom as the order the acts actually happen in rather
 * than as a menu grouped by verb.
 *
 * Every one is rendered even when it cannot be used (law 18): a control an
 * operator cannot see is one they cannot ask to be granted, and this whole
 * screen exists because somebody could not find the act anywhere and went to a
 * shell. The only thing that varies is `blocked` and whether `submit` is
 * attached at all.
 */
export function jurisdictionControls(
  api: ApiClient, caller: Caller,
): (rows: readonly GateRow[]) => readonly Block[] {
  return (rows) => [
    declareForm(api, caller),
    openForm(api, caller, rows),
    closeForm(api, caller, rows),
    widenForm(api, caller, rows),
    narrowForm(api, caller, rows),
  ]
}
